import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';

export interface PowSolution {
  c: string;
  s: string;
  n: number;
}

// Runs inside a Web Worker so solving never blocks the page. Plain SHA-256 instead
// of crypto.subtle: no per-hash promise overhead, which is several times faster.
// Tries nonces first, first + step, first + 2 * step, ... so workers don't overlap.
const WORKER_SOURCE = `
const K = new Uint32Array([
  0x428a2f98,0x71374491,0xb5c0fbcf,0xe9b5dba5,0x3956c25b,0x59f111f1,0x923f82a4,0xab1c5ed5,
  0xd807aa98,0x12835b01,0x243185be,0x550c7dc3,0x72be5d74,0x80deb1fe,0x9bdc06a7,0xc19bf174,
  0xe49b69c1,0xefbe4786,0x0fc19dc6,0x240ca1cc,0x2de92c6f,0x4a7484aa,0x5cb0a9dc,0x76f988da,
  0x983e5152,0xa831c66d,0xb00327c8,0xbf597fc7,0xc6e00bf3,0xd5a79147,0x06ca6351,0x14292967,
  0x27b70a85,0x2e1b2138,0x4d2c6dfc,0x53380d13,0x650a7354,0x766a0abb,0x81c2c92e,0x92722c85,
  0xa2bfe8a1,0xa81a664b,0xc24b8b70,0xc76c51a3,0xd192e819,0xd6990624,0xf40e3585,0x106aa070,
  0x19a4c116,0x1e376c08,0x2748774c,0x34b0bcb5,0x391c0cb3,0x4ed8aa4a,0x5b9cca4f,0x682e6ff3,
  0x748f82ee,0x78a5636f,0x84c87814,0x8cc70208,0x90befffa,0xa4506ceb,0xbef9a3f7,0xc67178f2
]);
const W = new Uint32Array(64);

function sha256(msg) {
  const len = msg.length;
  const padded = new Uint8Array(((len + 9 + 63) >> 6) << 6);
  padded.set(msg);
  padded[len] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 8, Math.floor(len / 0x20000000));
  view.setUint32(padded.length - 4, (len << 3) >>> 0);

  let h0 = 0x6a09e667, h1 = 0xbb67ae85, h2 = 0x3c6ef372, h3 = 0xa54ff53a;
  let h4 = 0x510e527f, h5 = 0x9b05688c, h6 = 0x1f83d9ab, h7 = 0x5be0cd19;

  for (let offset = 0; offset < padded.length; offset += 64) {
    for (let i = 0; i < 16; i++) W[i] = view.getUint32(offset + i * 4);
    for (let i = 16; i < 64; i++) {
      const w15 = W[i - 15], w2 = W[i - 2];
      const s0 = ((w15 >>> 7) | (w15 << 25)) ^ ((w15 >>> 18) | (w15 << 14)) ^ (w15 >>> 3);
      const s1 = ((w2 >>> 17) | (w2 << 15)) ^ ((w2 >>> 19) | (w2 << 13)) ^ (w2 >>> 10);
      W[i] = (W[i - 16] + s0 + W[i - 7] + s1) | 0;
    }
    let a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g = h6, h = h7;
    for (let i = 0; i < 64; i++) {
      const S1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7));
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + K[i] + W[i]) | 0;
      const S0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10));
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) | 0;
      h = g; g = f; f = e; e = (d + t1) | 0; d = c; c = b; b = a; a = (t1 + t2) | 0;
    }
    h0 = (h0 + a) | 0; h1 = (h1 + b) | 0; h2 = (h2 + c) | 0; h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0; h5 = (h5 + f) | 0; h6 = (h6 + g) | 0; h7 = (h7 + h) | 0;
  }
  return [h0, h1, h2, h3, h4, h5, h6, h7];
}

function leadingZeroBits(words) {
  let bits = 0;
  for (const word of words) {
    if (word === 0) { bits += 32; continue; }
    return bits + Math.clz32(word);
  }
  return bits;
}

// Workers report their attempts and busy time (they do nothing but hash, so
// this is their CPU time) regularly, and exactly when they find the nonce.
const REPORT_EVERY = 16384;

self.onmessage = (event) => {
  const { challenge, bits, first, step } = event.data;
  const encoder = new TextEncoder();
  const started = performance.now();
  let attempts = 0;
  for (let nonce = first; ; nonce += step) {
    attempts++;
    if (leadingZeroBits(sha256(encoder.encode(challenge + ':' + nonce))) >= bits) {
      self.postMessage({ nonce, done: true, attempts, ms: performance.now() - started });
      return;
    }
    if (attempts % REPORT_EVERY === 0) {
      self.postMessage({ done: false, attempts, ms: performance.now() - started });
    }
  }
};
`;

/**
 * Proof of work that gates the OAuth flows (see api/account/pow.php): fetches a
 * challenge and finds a nonce such that SHA-256("<challenge>:<nonce>") starts
 * with the requested number of zero bits.
 */
@Injectable({
  providedIn: 'root'
})
export class PowService {

  private api = inject(ApiService);

  async solve(purpose: 'login' | 'patreon' | 'gamenight' | 'ai'): Promise<PowSolution> {
    const challenge: any = await firstValueFrom(this.api.get('/account/pow_challenge.php?purpose=' + purpose));
    const started = performance.now();
    const { nonce, workers, attempts, cpuMs } = await this.findNonce(challenge.challenge, challenge.bits);
    const wallMs = performance.now() - started;
    console.log(
      `[PoW] ${purpose}: solved ${challenge.bits}-bit challenge in ${wallMs.toFixed(0)} ms wall time, ` +
      `~${cpuMs.toFixed(0)} ms CPU time across ${workers} worker(s), ` +
      `~${attempts.toLocaleString()} attempts (${(attempts / (cpuMs / 1000)).toLocaleString(undefined, { maximumFractionDigits: 0 })} hashes/s per worker-second), nonce ${nonce}`
    );
    return { c: challenge.challenge, s: challenge.signature, n: nonce };
  }

  private findNonce(challenge: string, bits: number): Promise<{ nonce: number, workers: number, attempts: number, cpuMs: number }> {
    const workerCount = Math.min(4, Math.max(1, (navigator.hardwareConcurrency || 2) - 1));
    const url = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: 'text/javascript' }));

    return new Promise((resolve, reject) => {
      const workers: Worker[] = [];
      // Last report of each worker; workers that get terminated mid-batch are slightly under-counted
      const reports = Array.from({ length: workerCount }, () => ({ attempts: 0, ms: 0 }));
      const finish = (done: () => void) => {
        workers.forEach(worker => worker.terminate());
        URL.revokeObjectURL(url);
        done();
      };

      for (let first = 0; first < workerCount; first++) {
        const worker = new Worker(url);
        workers.push(worker);
        worker.onmessage = (event: MessageEvent<{ nonce?: number, done: boolean, attempts: number, ms: number }>) => {
          reports[first] = { attempts: event.data.attempts, ms: event.data.ms };
          if (event.data.done) {
            finish(() => resolve({
              nonce: event.data.nonce!,
              workers: workerCount,
              attempts: reports.reduce((sum, r) => sum + r.attempts, 0),
              cpuMs: reports.reduce((sum, r) => sum + r.ms, 0)
            }));
          }
        };
        worker.onerror = (event: ErrorEvent) => finish(() => reject(event.error ?? new Error(event.message)));
        worker.postMessage({ challenge, bits, first, step: workerCount });
      }
    });
  }
}
